import { FigureSelectionArea } from 'features/fde/regionTypes'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { parseFfsStats } from 'features/indicationTriage/customInfoTypes/ffsStats'
import { isLengthUnit } from 'types'

import {
  ClassificationResult,
  ComponentType,
} from './ffsComponentClassification'
import { CorrosionType, EntryRoute } from './ffsRegionClassificationSchema'
import {
  classifyCorrosionType,
  classifyFlawShape,
  componentTypeEligibility,
  CorrosionBasis,
  entryRouteToPartLevel,
  FlawShape,
  selectEntryRoute,
} from './ffsRegionRouting'
import { FfsPart } from './ffsTypes'

/**
 * The pure decision layer behind the FFS pre-planning form: given the classifier
 * result, the corrosion type, and whether a critical-thickness profile exists,
 * what single state is this LTA in?
 *
 * This exists so the routing/gating logic is one discriminated value instead of
 * a handful of interdependent booleans re-branched inside JSX — and so it is
 * unit-testable without React.
 *
 * This module is also the SINGLE owner of the critical-thickness-profile gate:
 * `selectEntryRoute` is deliberately profile-blind, so the "local metal loss
 * needs a profile" rule is implemented here and nowhere else.
 */

/**
 * Whether a usable critical-thickness profile exists for an LTA. Lives here (not
 * in `ffsRegionRouting`) because `computeLtaDecision` is the only thing that
 * branches on it; `useLtaProfileStatus` produces it.
 */
export type ProfileStatus = 'loading' | 'unavailable' | 'usable'

/** A part+level the engineer *may* use beyond the recommended route. */
export interface PermittedPartLevel {
  part: FfsPart
  level: 'level1' | 'level2'
}

/**
 * The one state an LTA's pre-planning decision is in. Deliberately NOT a bag of
 * booleans: these states are mutually exclusive and each carries exactly the data
 * its presentation needs.
 */
export type LtaDecision =
  | {
      /** Component type or corrosion type not yet determined — keep asking. */
      kind: 'incomplete'
    }
  | {
      /**
       * A groove-like flaw: outside this tool's scope, whatever the component
       * type. Terminal, like `routed` with `GROOVE_NOT_COVERED` — but kept as
       * its own state (not folded into `routed`) so the view never has to
       * special-case a "routed" decision that in fact routes nowhere.
       *
       * Carries `componentType` + `route` (route is always `GROOVE_NOT_COVERED`)
       * because this decision IS committed to a persisted classification, even
       * though it never reaches an assessable Part/Level: without a saved
       * record, `computeFfsEligibility` sees no classification at all and
       * PERMISSIVELY unlocks every level — the opposite of flagging a groove.
       */
      kind: 'groove-not-covered'
      componentType: ComponentType
      route: EntryRoute
    }
  | {
      /** Local metal loss on an A/B1 region; the CTP query is still running. */
      kind: 'profile-loading'
    }
  | {
      /** Local metal loss on an A/B1 region with no usable CTP — cannot route. */
      kind: 'blocked-on-profile'
    }
  | {
      /** Fully determined: a committable route plus permitted alternatives. */
      kind: 'routed'
      componentType: ComponentType
      route: EntryRoute
      /**
       * Structured, NOT display strings — formatting is the view's job, so this
       * module stays free of UI wording.
       */
      permittedOthers: ReadonlyArray<PermittedPartLevel>
    }

/**
 * Permitted alternatives beyond the recommended route: every type-eligible
 * part+level except the recommended one.
 */
function permittedAlternatives(
  componentType: ComponentType,
  route: EntryRoute
): ReadonlyArray<PermittedPartLevel> {
  const eligibility = componentTypeEligibility(componentType)
  const recommended = entryRouteToPartLevel(route)
  const others: PermittedPartLevel[] = []
  for (const part of [FfsPart.PART_4, FfsPart.PART_5]) {
    for (const level of ['level1', 'level2'] as const) {
      if (!eligibility[part][level].eligible) continue
      if (recommended?.part === part && recommended.level === level) continue
      others.push({ part, level })
    }
  }
  return others
}

/**
 * Collapse the classifier result + corrosion type + profile status into one
 * decision.
 *
 * `incomplete` means "facts or corrosion type still missing"; the two profile
 * states mean "determined, but a local-metal-loss region has no usable
 * critical-thickness profile yet". Keeping them distinct matters: reporting a
 * missing profile as `incomplete` would tell the engineer to answer more
 * questions when the real blocker is a missing CTP. `groove-not-covered` is
 * distinct again: it is fully determined (unlike `incomplete`) and IS
 * committable to a persisted classification (like `routed`) — but that
 * classification's route (`GROOVE_NOT_COVERED`) never resolves to an
 * assessable Part/Level, whatever the component type.
 */
export function computeLtaDecision({
  classification,
  corrosionType,
  profileStatus,
}: {
  classification: ClassificationResult
  corrosionType: CorrosionType | null
  profileStatus: ProfileStatus
}): LtaDecision {
  const componentType =
    classification.status === 'resolved' ? classification.type : null
  if (componentType === null || corrosionType === null) {
    return { kind: 'incomplete' }
  }

  // A groove-like flaw is a separate out-of-scope procedure, whatever the
  // component type — checked right after `incomplete` so it takes priority
  // over the profile gate below (grooves never need a CTP either). Still
  // resolves `componentType` + `route` (route is always `GROOVE_NOT_COVERED`)
  // so the form can commit this decision exactly like a `routed` one.
  if (corrosionType === 'groove-like') {
    return {
      kind: 'groove-not-covered',
      componentType,
      route: selectEntryRoute({ componentType, corrosionType }),
    }
  }

  // A local-corrosion A/B1 region needs a critical-thickness profile to route to
  // Part 5. Distinguish "still loading" (neutral) from "confirmed unavailable"
  // (blocked) so the warning can't flash while the CTP query runs. This is the
  // only place the profile gate is applied.
  const localProfileGated =
    corrosionType === 'local' &&
    (componentType === 'A' || componentType === 'B1')
  if (localProfileGated && profileStatus === 'loading') {
    return { kind: 'profile-loading' }
  }
  if (localProfileGated && profileStatus === 'unavailable') {
    return { kind: 'blocked-on-profile' }
  }

  const route = selectEntryRoute({ componentType, corrosionType })
  return {
    kind: 'routed',
    componentType,
    route,
    permittedOthers: permittedAlternatives(componentType, route),
  }
}

/** Per-LTA wall thickness in the working unit (mm), or nulls when unavailable. */
export interface LtaThickness {
  /** Minimum measured wall thickness, t_mm. */
  tMmMm: number | null
  /** Average measured wall thickness, t_am. */
  tAmMm: number | null
}

/** Per-LTA planform extents in the working unit (mm), or nulls. */
export interface FlawLengths {
  /** The LONGER of the two footprint extents. */
  lengthMm: number | null
  /** The SHORTER of the two footprint extents. */
  widthMm: number | null
}

export interface DerivedFlawShape {
  lengths: FlawLengths
  /** `null` when the selection geometry is missing/unusable. */
  flawShape: FlawShape | null
}

export interface DerivedCorrosion extends DerivedFlawShape {
  thickness: LtaThickness
  /** `null` when the inputs cannot decide — the engineer then classifies. */
  derivedCorrosion: CorrosionType | null
  /** WHICH gate decided, for the readout. Never re-derive this from the numbers. */
  basis: CorrosionBasis
}

/**
 * Derive the planform shape from an LTA's rectangular `selection` (§5.2.1).
 *
 * Length and width are the LONGER and SHORTER footprint extents, not the
 * longitudinal and circumferential ones — so this needs no component model and
 * no axis lookup, and a groove is caught whichever way it runs. Both are
 * converted to `WORKING_UNIT_THICKNESS` (reused here as the working LENGTH unit)
 * so the ratio is unit-free.
 *
 * `isLengthUnit` is not optional politeness: `convertNumber` returns a unit it
 * does not recognise UNCHANGED, so a selection in tubes or frames would divide
 * tube counts by millimetres and yield a confident garbage ratio. Missing or
 * unconvertible geometry yields all-`null`.
 */
export function deriveFlawShape({
  selection,
}: {
  selection: FigureSelectionArea | undefined
}): DerivedFlawShape {
  if (
    !selection ||
    !isLengthUnit(selection.x.unit) ||
    !isLengthUnit(selection.y.unit)
  ) {
    return { lengths: { lengthMm: null, widthMm: null }, flawShape: null }
  }

  const extentX = selection.x.size.toNumber(WORKING_UNIT_THICKNESS)
  const extentY = selection.y.size.toNumber(WORKING_UNIT_THICKNESS)
  const lengths: FlawLengths = {
    lengthMm: Math.max(extentX, extentY),
    widthMm: Math.min(extentX, extentY),
  }
  return { lengths, flawShape: classifyFlawShape(lengths) }
}

/**
 * Derive the corrosion type from an LTA's raw `customInfo.ffsStats` blob AND its
 * selection footprint — the two gates of Rule 1.
 *
 * Each stat carries its own unit (the CDF's), so both are converted to
 * `WORKING_UNIT_THICKNESS` (mm) before the §4.3.3.2 Eq. 4.1 uniformity test.
 * Absent or malformed inputs yield `null` — undetermined, not an error.
 *
 * Returns the thickness figures, the footprint, AND the shape alongside the
 * verdict so the readout can say WHICH gate decided without recomputing any of
 * it — that explanation is the only place the shape survives, since it is
 * deliberately not persisted.
 */
export function deriveCorrosion({
  rawFfsStats,
  selection,
}: {
  rawFfsStats: unknown
  selection: FigureSelectionArea | undefined
}): DerivedCorrosion {
  const ffsStats = parseFfsStats(rawFfsStats)
  const thickness: LtaThickness = {
    tMmMm: ffsStats ? ffsStats.minimum.toNumber(WORKING_UNIT_THICKNESS) : null,
    tAmMm: ffsStats ? ffsStats.mean.toNumber(WORKING_UNIT_THICKNESS) : null,
  }
  const { lengths, flawShape } = deriveFlawShape({ selection })
  const { corrosionType, basis } = classifyCorrosionType({
    ...thickness,
    flawShape,
  })
  return {
    thickness,
    lengths,
    flawShape,
    derivedCorrosion: corrosionType,
    basis,
  }
}
